import React from 'react';
import { nowChile } from '../lib/utils';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isWithinInterval,
  parseISO, addWeeks, subWeeks, startOfDay
} from 'date-fns';
import { es } from 'date-fns/locale';

export function CalendarView({ tasks, date, setDate, view, setView, onEditTask }) {
  const today = nowChile();
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });

  const weeks = [];
  let d = calStart;
  while (d <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(d));
      d = addDays(d, 1);
    }
    weeks.push(week);
  }

  const weekDays = Array.from({ length: 7 }, function(_, i) { return addDays(weekStart, i); });

  const getTasksForDay = function(day) {
    return tasks.filter(function(t) {
      if (!t.start_date) return false;
      const s = startOfDay(parseISO(t.start_date));
      const e = t.end_date ? startOfDay(parseISO(t.end_date)) : s;
      return isWithinInterval(startOfDay(day), { start: s, end: e });
    });
  };

  const navPrev = function() {
    if (view === 'month') setDate(subMonths(date, 1));
    else setDate(subWeeks(date, 1));
  };
  const navNext = function() {
    if (view === 'month') setDate(addMonths(date, 1));
    else setDate(addWeeks(date, 1));
  };

  const headerText = view === 'month'
    ? format(date, 'MMMM yyyy', { locale: es })
    : format(weekStart, 'dd MMM', { locale: es }) + ' - ' + format(addDays(weekStart, 6), 'dd MMM yyyy', { locale: es });

  const dayNames = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

  const renderDayCell = function(dayDate, idx) {
    const dayTasks = getTasksForDay(dayDate);
    const isToday = isSameDay(dayDate, today);
    const isCur = isSameMonth(dayDate, date);
    const dayNumClass = isToday
      ? 'w-6 h-6 flex items-center justify-center rounded-full bg-[#7FD02E] text-white font-bold text-xs mb-1'
      : isCur ? 'text-xs mb-1 text-gray-700' : 'text-xs mb-1 text-gray-300';

    return (
      <div key={idx} className={'min-h-[80px] border-b border-r border-gray-50 p-1 ' + (!isCur ? 'bg-gray-50/50' : 'hover:bg-gray-50')}>
        <div className={dayNumClass}>{format(dayDate, 'd')}</div>
        <div className="space-y-0.5">
          {dayTasks.slice(0, 3).map(function(t) {
            const prefix = (t._depth || 0) > 0 ? '\u21B3 ' : '';
            const ml = (t._depth || 0) * 4;
            return (
              <div
                key={t.id}
                className="text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80 text-white font-medium"
                style={{ backgroundColor: t.color || '#3B82F6', marginLeft: ml + 'px' }}
                onClick={function() { onEditTask(t); }}
                title={t.name}
              >
                {prefix}{t.name}
              </div>
            );
          })}
          {dayTasks.length > 3 && (
            <div className="text-[10px] text-gray-400 px-1">+{dayTasks.length - 3}</div>
          )}
        </div>
      </div>
    );
  };

  const renderWeekCell = function(dayDate, idx) {
    const dayTasks = getTasksForDay(dayDate);
    const isToday = isSameDay(dayDate, today);
    const dayClass = isToday
      ? 'text-sm mb-2 text-center w-8 h-8 flex items-center justify-center rounded-full bg-[#7FD02E] text-white font-bold mx-auto'
      : 'text-sm mb-2 text-center text-gray-700';

    return (
      <div key={idx} className="min-h-[200px] border-r border-gray-50 p-2">
        <div className={dayClass}>{format(dayDate, 'd')}</div>
        <div className="space-y-1">
          {dayTasks.map(function(t) {
            const prefix = (t._depth || 0) > 0 ? '\u21B3 ' : '';
            const names = (t.assignees || []).map(function(a) { return a.user ? a.user.first_name : ''; }).filter(Boolean).join(', ');
            return (
              <div key={t.id} className="text-xs px-2 py-1.5 rounded cursor-pointer hover:opacity-80 text-white font-medium" style={{ backgroundColor: t.color || '#3B82F6' }} onClick={function() { onEditTask(t); }}>
                <div className="truncate">{prefix}{t.name}</div>
                {names && <div className="text-[10px] opacity-80 truncate">{names}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm" data-testid="calendar-view">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={navPrev} className="h-8 w-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="font-heading text-lg font-semibold text-gray-800 min-w-[180px] text-center capitalize">
              {headerText}
            </h3>
            <Button variant="ghost" size="icon" onClick={navNext} className="h-8 w-8">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            <Button
              variant={view === 'week' ? 'default' : 'ghost'} size="sm"
              className={'h-7 text-xs ' + (view === 'week' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500')}
              onClick={function() { setView('week'); }}
            >Semana</Button>
            <Button
              variant={view === 'month' ? 'default' : 'ghost'} size="sm"
              className={'h-7 text-xs ' + (view === 'month' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500')}
              onClick={function() { setView('month'); }}
            >Mes</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-7 border-b border-gray-100">
          {dayNames.map(function(dn) {
            return <div key={dn} className="py-2 text-center text-xs font-medium text-gray-400 uppercase">{dn}</div>;
          })}
        </div>
        {view === 'month' ? (
          <div className="grid grid-cols-7">
            {weeks.flat().map(renderDayCell)}
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {weekDays.map(renderWeekCell)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
